import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import "../../assets/styles/ClientShared.css";

const acceptanceDefaults = {
  contractSigned: false,
  depositPaid: false,
  notes: "",
  completedAt: null,
};

const formatLink = (value) => {
  if (!value) return "";
  return value.startsWith("http") ? value : `https://${value}`;
};

const ProjectAcceptance = () => {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [projectData, setProjectData] = useState(null);
  const [acceptance, setAcceptance] = useState(acceptanceDefaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        const projectSnap = await getDoc(doc(db, "projects", userData.projectId));
        if (!projectSnap.exists()) throw new Error("Project document not found.");

        const data = projectSnap.data();
        setProjectId(userData.projectId);
        setProjectData(data);
        setAcceptance({ ...acceptanceDefaults, ...(data.clientAcceptance || {}) });
      } catch (error) {
        console.error("Error loading project acceptance:", error);
        setMessage("Unable to load your project acceptance details. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, []);

  const updateAcceptance = (field, value) => {
    setAcceptance(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const isComplete = acceptance.contractSigned && acceptance.depositPaid;
      const updatedAcceptance = {
        ...acceptance,
        completedAt: isComplete ? (acceptance.completedAt || new Date().toISOString()) : null,
      };
      const updatedActions = (projectData.actions || []).map(action =>
        action.link === "/client/project-acceptance"
          ? { ...action, complete: isComplete }
          : action
      );

      await updateDoc(doc(db, "projects", projectId), {
        clientAcceptance: updatedAcceptance,
        contractSigned: updatedAcceptance.contractSigned,
        depositPaid: updatedAcceptance.depositPaid,
        projectAccepted: isComplete,
        actions: updatedActions,
      });

      setAcceptance(updatedAcceptance);
      setProjectData(prev => ({ ...prev, clientAcceptance: updatedAcceptance, actions: updatedActions }));
      setMessage(isComplete ? "Acceptance saved. Your onboarding steps are ready." : "Progress saved.");
    } catch (error) {
      console.error("Error saving project acceptance:", error);
      setMessage("Unable to save right now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cp-loading"><p>Loading acceptance details...</p></div>;

  const proposalDocuments = projectData?.proposalDocuments || {};
  const proposal = proposalDocuments.proposal || {};
  const contractLink = formatLink(proposalDocuments.contract);
  const invoiceLink = formatLink(proposalDocuments.invoice);
  const isComplete = acceptance.contractSigned && acceptance.depositPaid;

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Approve Your Project</h1>
        <p className="cp-subtitle">Review the proposal, sign the contract, and pay the deposit so your project can move into onboarding.</p>
      </div>

      {message && <p className="cp-message" style={{ marginBottom: "20px" }}>{message}</p>}

      <div className="cp-section" style={{ marginBottom: "20px" }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">{proposal.service || projectData?.packageDisplayName || "Website Project"}</h2>
          {isComplete ? <span className="cp-badge cp-badge--active">Accepted</span> : <span className="cp-badge cp-badge--pending">Action Needed</span>}
        </div>
        <div className="cp-section-body">
          <ul className="cp-list">
            <li className="cp-list-item"><strong>Project:</strong> {projectData?.projectName || "Website project"}</li>
            <li className="cp-list-item"><strong>Investment:</strong> ${proposal.investment || projectData?.price || "TBD"}</li>
            <li className="cp-list-item"><strong>Payment plan:</strong> {projectData?.paymentPlan || "TBD"}</li>
          </ul>
        </div>
      </div>

      <div className="cp-section" style={{ marginBottom: "20px" }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Approval Steps</h2>
        </div>
        <div className="cp-section-body">
          <div className="cp-doc-list">
            <div className="cp-doc-item">
              <span className="cp-doc-label">Proposal</span>
              <button className="cp-doc-link" onClick={() => navigate(`/proposal/${projectId}`)}>View &rarr;</button>
            </div>
            <div className="cp-doc-item">
              <span className="cp-doc-label">Contract</span>
              {contractLink ? <a className="cp-doc-link" href={contractLink} target="_blank" rel="noopener noreferrer">Sign &rarr;</a> : <span className="cp-empty-text">Link pending</span>}
            </div>
            <div className="cp-doc-item">
              <span className="cp-doc-label">Deposit Invoice</span>
              {invoiceLink ? <a className="cp-doc-link" href={invoiceLink} target="_blank" rel="noopener noreferrer">Pay &rarr;</a> : <span className="cp-empty-text">Link pending</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="cp-section">
        <div className="cp-section-header">
          <h2 className="cp-section-title">Confirm Completion</h2>
        </div>
        <div className="cp-section-body">
          <div className="cp-checklist">
            <label className="cp-checklist-item">
              <input type="checkbox" checked={acceptance.contractSigned} onChange={e => updateAcceptance("contractSigned", e.target.checked)} />
              <span>I signed the contract.</span>
            </label>
            <label className="cp-checklist-item">
              <input type="checkbox" checked={acceptance.depositPaid} onChange={e => updateAcceptance("depositPaid", e.target.checked)} />
              <span>I paid the deposit or first subscription invoice.</span>
            </label>
          </div>

          <div className="cp-form-group" style={{ marginTop: "20px" }}>
            <label>Notes for Next Level Websites</label>
            <textarea value={acceptance.notes} onChange={e => updateAcceptance("notes", e.target.value)} placeholder="Optional: receipt note, billing question, project window preference, or anything you want confirmed." />
          </div>

          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Approval Status"}
            </button>
            {isComplete && <button className="cp-btn-secondary" onClick={() => navigate("/client")}>Back to Dashboard</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectAcceptance;
